import { Controller } from "@hotwired/stimulus";
import { Stimulus } from "../assets/javascripts/stimulus";
import { fallbackPosition } from "./dropdown_component/fallback_position";
import { nextIndex, typeaheadIndex } from "./dropdown_component/navigation";

const IDENTIFIER = "dropdown-component";
const HOST = `[data-controller~="${IDENTIFIER}"]`;
const ARROW_KEYS = ["ArrowDown", "ArrowUp", "Home", "End"];
const DEFAULT_GAP = 2;
// Elements a mousedown may focus (labels focus their control on click).
const FOCUSABLE =
  "a[href], button, input, select, textarea, label, summary, iframe, [tabindex], [contenteditable]:not([contenteditable='false'])";

// Behavior for DropdownComponent.
//
// The menu is an `auto` popover (button trigger), a `manual` popover (input
// trigger) or, with `popover: false`, an inline element toggled with
// `hidden`. For auto popovers the browser opens and closes the menu (the
// popovertarget invoker, outside clicks, Escape, one open at a time,
// nesting); this controller syncs aria-expanded, dispatches events, closes
// on item choice and Tab out, and returns focus. Manual and inline menus get
// their outside-click and Escape handling here.
export default class DropdownComponentController extends Controller {
  static targets = ["trigger", "menu"];
  static values = { mode: String, open: Boolean, forceFallback: Boolean };

  // The controller of the dropdown that contains `element`, or null.
  static for(element) {
    const host = element?.closest?.(HOST);
    if (!host) return null;

    const application = window.Stimulus || Stimulus;
    return application.getControllerForElementAndIdentifier(host, IDENTIFIER);
  }

  #closeReason = null;
  #openedBy = null;
  #announcedOpen = false;
  #tabbing = false;
  #tabbingFrame = null;
  #hideTimer = null;
  #restoringFocus = false;
  #dismissing = false;
  #watchingEscape = false;
  #watchingMenuKeys = false;
  #escapeCheck = null;
  #bound = null;
  #fallback = null;

  connect() {
    if (!this.hasMenuTarget) return;

    const menu = this.menuTarget;
    menu.dataset.position = this.#anchorSupported() ? "anchor" : "fallback";

    // A unique anchor name per instance, set as CSSOM properties: one shared
    // name in CSS would anchor every row's menu to the last trigger.
    const anchorName = `--dropdown-${menu.id}`;
    if (this.hasTriggerTarget) this.triggerTarget.style.anchorName = anchorName;
    menu.style.positionAnchor = anchorName;

    this.#bind();

    // A page restored from a cache, or a re-render, can carry a stale
    // aria-expanded: read the state rather than assume it.
    this.#announcedOpen = this.isOpen;
    this.#syncExpanded();
    if (this.#announcedOpen) {
      this.#addDismissListeners();
      this.#watchEscape();
      this.#watchMenuKeys();
      this.#startFallback();
    }

    if (this.openValue) this.show();
  }

  disconnect() {
    this.#unbind();
    this.#removeDismissListeners();
    this.#unwatchEscape();
    this.#unwatchMenuKeys();
    this.#stopFallback();
    this.#cancelHide();
    this.#clearTabbing();
  }

  get isOpen() {
    if (!this.hasMenuTarget) return false;
    if (this.#inline) return !this.menuTarget.hidden;
    return this.menuTarget.matches(":popover-open");
  }

  show() {
    if (!this.hasMenuTarget || this.isOpen) return;

    if (this.#inline) {
      if (!this.#dispatchShow()) return;
      this.menuTarget.hidden = false;
      this.#opened();
      return;
    }

    // beforetoggle dispatches dropdown-component:show and can cancel this.
    const options = this.hasTriggerTarget ? { source: this.triggerTarget } : {};
    this.menuTarget.showPopover(options);
  }

  hide() {
    this.#cancelHide();
    if (!this.hasMenuTarget || !this.isOpen) {
      this.#closeReason = null;
      return;
    }

    if (this.#inline) {
      this.menuTarget.hidden = true;
      this.#closed();
      return;
    }

    this.menuTarget.hidePopover();
  }

  toggle() {
    this.isOpen ? this.hide() : this.show();
  }

  // Listeners are bound to the elements present at connect, and removed
  // from those same elements, so a target that has since left the DOM does
  // not break disconnect.
  #bind() {
    const trigger = this.hasTriggerTarget ? this.triggerTarget : null;
    const menu = this.menuTarget;
    this.#bound = { trigger, menu };

    this.element.addEventListener("click", this.#blockDisabled, true);
    this.element.addEventListener("keydown", this.#blockDisabledKeys, true);
    this.element.addEventListener("click", this.#onClick);
    this.element.addEventListener("keydown", this.#noteTabbing, true);
    this.element.addEventListener("keydown", this.#onKeydown);
    this.element.addEventListener("keyup", this.#clearTabbing);
    this.element.addEventListener("focusout", this.#onFocusout);
    trigger?.addEventListener("click", this.#onTriggerClick);
    trigger?.addEventListener("focus", this.#onTriggerFocus);
    menu.addEventListener("beforetoggle", this.#onBeforeToggle);
    menu.addEventListener("toggle", this.#onToggle);
    menu.addEventListener("mousedown", this.#onMenuMousedown);
  }

  #unbind() {
    if (!this.#bound) return;
    const { trigger, menu } = this.#bound;
    this.#bound = null;

    this.element.removeEventListener("click", this.#blockDisabled, true);
    this.element.removeEventListener("keydown", this.#blockDisabledKeys, true);
    this.element.removeEventListener("click", this.#onClick);
    this.element.removeEventListener("keydown", this.#noteTabbing, true);
    this.element.removeEventListener("keydown", this.#onKeydown);
    this.element.removeEventListener("keyup", this.#clearTabbing);
    this.element.removeEventListener("focusout", this.#onFocusout);
    trigger?.removeEventListener("click", this.#onTriggerClick);
    trigger?.removeEventListener("focus", this.#onTriggerFocus);
    menu.removeEventListener("beforetoggle", this.#onBeforeToggle);
    menu.removeEventListener("toggle", this.#onToggle);
    menu.removeEventListener("mousedown", this.#onMenuMousedown);
  }

  // Manual and inline menus have no light dismiss, so they listen to the
  // document while open.
  #addDismissListeners() {
    if (!this.#selfDismissing || this.#dismissing) return;
    document.addEventListener("pointerdown", this.#onDocumentPointerdown, true);
    document.addEventListener("keydown", this.#onDocumentKeydown);
    this.#dismissing = true;
  }

  #removeDismissListeners() {
    if (!this.#dismissing) return;
    document.removeEventListener(
      "pointerdown",
      this.#onDocumentPointerdown,
      true,
    );
    document.removeEventListener("keydown", this.#onDocumentKeydown);
    this.#dismissing = false;
  }

  #onTriggerClick = (event) => {
    if (!this.#owns(event)) return;

    // A click from Enter or Space has no click count.
    this.#openedBy = event.detail === 0 ? "keyboard" : "pointer";

    if (this.#manual) {
      this.show();
    } else if (this.#inline) {
      event.preventDefault();
      this.toggle();
    }
  };

  #onTriggerFocus = () => {
    if (this.#manual && !this.#restoringFocus) this.show();
  };

  #onBeforeToggle = (event) => {
    if (event.newState === "open" && !this.#dispatchShow())
      event.preventDefault();
  };

  // Toggle events are coalesced, so read the state instead of trusting
  // event.newState.
  #onToggle = () => {
    if (this.isOpen) {
      this.#opened();
    } else {
      this.#closed();
    }
  };

  #opened() {
    this.#syncExpanded();
    if (this.#announcedOpen) return;
    this.#announcedOpen = true;

    this.#addDismissListeners();
    this.#watchEscape();
    this.#watchMenuKeys();
    this.#startFallback();
    this.#focusOnOpen();
    this.#dispatch("shown");
  }

  #closed() {
    this.#syncExpanded();
    this.#cancelHide();
    this.#removeDismissListeners();
    this.#unwatchEscape();
    this.#unwatchMenuKeys();
    this.#stopFallback();
    this.#resetTabindexes();
    this.#openedBy = null;

    const reason = this.#closeReason;
    this.#closeReason = null;
    if (!this.#announcedOpen) return;
    this.#announcedOpen = false;

    if (reason) this.#restoreFocus();
    this.#dispatch("hidden");
  }

  #onClick = (event) => {
    if (!this.#owns(event) || !this.isOpen) return;
    if (!this.menuTarget.contains(event.target)) return;

    const selector =
      this.modeValue === "menu"
        ? '[role="menuitem"]'
        : "a[href], [data-dropdown-component-close]";
    const choice = event.target.closest(selector);
    if (!choice || !this.menuTarget.contains(choice)) return;

    // Close on the next task, so the click's own default action (following
    // a link, submitting a form) runs first.
    this.#cancelHide();
    this.#hideTimer = setTimeout(() => {
      this.#hideTimer = null;
      this.#close("item");
    });
  };

  #onKeydown = (event) => {
    if (!this.#owns(event)) return;
    if (!this.isOpen) return this.#openFromTrigger(event);

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.#close("escape");
    } else if (event.key === "Tab" && this.#isMenuItem(event.target)) {
      event.stopPropagation();
      this.#tabFromItem(event);
    } else if (this.#inMenu(event.target)) {
      this.#navigate(event);
    }
  };

  // ArrowDown and ArrowUp on a closed menu's trigger open it and focus the
  // first or last item. Enter and Space open it through the native click.
  #openFromTrigger(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    if (this.modeValue !== "menu" || this.#selfDismissing) return;
    if (!this.hasTriggerTarget || event.target !== this.triggerTarget) return;

    this.#consume(event);
    this.show();
    if (this.isOpen) this.#focusEdge(event.key);
  }

  // Keys inside an open menu: arrows, Home and End move focus (disabled
  // items included, hidden ones skipped), a letter jumps to the next item
  // starting with it, and Space activates a link as Enter does.
  #navigate(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const key = event.key;

    if (ARROW_KEYS.includes(key)) {
      this.#consume(event);
      const items = this.#visibleItems();
      const current = items.indexOf(document.activeElement);
      this.#focusItem(items, nextIndex(items.length, current, key));
    } else if (key === " ") {
      const item = event.target.closest('[role="menuitem"]');
      if (item?.tagName !== "A") return;
      this.#consume(event);
      item.click();
    } else if (key.length === 1) {
      this.#consume(event);
      const items = this.#visibleItems();
      const labels = items.map((item) => item.textContent);
      const current = items.indexOf(document.activeElement);
      this.#focusItem(items, typeaheadIndex(labels, current, key));
    }
  }

  #consume(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Roving tabindex.
  #focusItem(items, index) {
    if (index < 0) return;
    items.forEach((item, i) => (item.tabIndex = i === index ? 0 : -1));
    items[index].focus();
  }

  #focusEdge(key) {
    const items = this.#visibleItems();
    this.#focusItem(items, nextIndex(items.length, -1, key));
  }

  // Every item, not just visible ones: this runs once the popover has
  // hidden, when no item reports visible.
  #resetTabindexes() {
    this.#items().forEach((item) => (item.tabIndex = -1));
  }

  // A mouse open leaves focus on the trigger or body (focusing the menu
  // resets an anchored menu's scroll in Chrome), so arrow keys from there
  // are picked up on the document. Only the innermost open menu acts.
  #watchMenuKeys() {
    if (this.modeValue !== "menu" || this.#manual || this.#watchingMenuKeys)
      return;
    document.addEventListener("keydown", this.#onDocumentMenuKey, true);
    this.#watchingMenuKeys = true;
  }

  #unwatchMenuKeys() {
    if (!this.#watchingMenuKeys) return;
    document.removeEventListener("keydown", this.#onDocumentMenuKey, true);
    this.#watchingMenuKeys = false;
  }

  #onDocumentMenuKey = (event) => {
    if (!ARROW_KEYS.includes(event.key) || !this.isOpen) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const target = event.target;
    const onTrigger = this.hasTriggerTarget && target === this.triggerTarget;
    if (!onTrigger && target !== document.body) return;
    if (this.#hasOpenNestedMenu()) return;

    this.#consume(event);
    this.#focusEdge(event.key);
  };

  #hasOpenNestedMenu() {
    return [...this.menuTarget.querySelectorAll(HOST)].some((host) =>
      host.querySelector("[popover]:popover-open"),
    );
  }

  // Tab or Shift+Tab on an item closes the menu. Shift+Tab lands on the
  // trigger. Tab moves on from the trigger, which, with the menu closed, is
  // the next focusable element after the component.
  #tabFromItem(event) {
    if (event.shiftKey) event.preventDefault();
    this.hide();
    this.#focusTrigger();
  }

  // Tab out closes the menu: when a Tab keypress moves focus outside the
  // component. Other focus moves never close it. This checks containment,
  // not ownership, so tabbing from a nested menu into the outer one closes
  // only the nested one.
  #onFocusout = (event) => {
    if (!this.#tabbing || !this.isOpen) return;
    if (this.element.contains(event.relatedTarget)) return;
    this.hide();
  };

  // A mousedown on non-focusable menu content (padding, a divider, a
  // heading) would focus the tabindex="-1" menu, and in Chrome 155 that
  // resets a scrolled anchored menu's scrollTop. Focus stays where it was.
  // The menu's own scrollbar keeps its default.
  #onMenuMousedown = (event) => {
    const menu = this.menuTarget;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target === menu && event.offsetX >= menu.clientWidth) return;

    const focusable = target.closest(FOCUSABLE);
    if (focusable && focusable !== menu && menu.contains(focusable)) return;
    event.preventDefault();
  };

  #onDocumentPointerdown = (event) => {
    if (this.element.contains(event.target)) return;
    this.hide();
  };

  // Escape from inside the component is handled (and stopped) by #onKeydown.
  #onDocumentKeydown = (event) => {
    if (event.key !== "Escape" || this.element.contains(event.target)) return;
    this.#close("escape");
  };

  // An auto popover closes natively on Escape wherever focus is, so Escape
  // from body (Safari does not focus a clicked button) or from a nested
  // dropdown's trigger never reaches #onKeydown. This notes the reason
  // first. If the key closed another popover instead, this one is still open
  // afterwards, and the stale reason is dropped.
  #watchEscape() {
    if (this.#selfDismissing || this.#watchingEscape) return;
    document.addEventListener("keydown", this.#onDocumentEscape, true);
    this.#watchingEscape = true;
  }

  #unwatchEscape() {
    if (!this.#watchingEscape) return;
    document.removeEventListener("keydown", this.#onDocumentEscape, true);
    this.#watchingEscape = false;
    this.#cancelEscapeCheck();
  }

  #onDocumentEscape = (event) => {
    if (event.key !== "Escape") return;
    const target = event.target;
    if (target !== document.body && !this.element.contains(target)) return;

    this.#closeReason = "escape";
    this.#cancelEscapeCheck();
    this.#escapeCheck = setTimeout(() => {
      this.#escapeCheck = null;
      if (this.isOpen) this.#closeReason = null;
    });
  };

  #cancelEscapeCheck() {
    if (this.#escapeCheck === null) return;
    clearTimeout(this.#escapeCheck);
    this.#escapeCheck = null;
  }

  // aria-disabled items are not natively disabled: block their clicks
  // (including a button's form submission) and their activation keys.
  #blockDisabled = (event) => {
    if (!this.#disabledItem(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  #blockDisabledKeys = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (!this.#disabledItem(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  #owns(event) {
    return event.target.closest?.(HOST) === this.element;
  }

  #close(reason) {
    this.#closeReason = reason;
    this.hide();
  }

  // A keyboard open of a menu focuses its first item. A programmatic show()
  // and an input trigger leave focus alone.
  //
  // A mouse open does not focus the menu container: in Chrome 155, with
  // anchor positioning, a style change to the menu (focus included) resets
  // its scrollTop, so the click on a scrolled item would miss. Arrow keys
  // after a mouse open are handled by #onDocumentMenuKey instead.
  #focusOnOpen() {
    const openedBy = this.#openedBy;
    this.#openedBy = null;
    if (openedBy !== "keyboard" || this.#manual) return;
    if (this.modeValue === "menu") {
      this.#focusEdge("Home");
    }
  }

  // Back to the trigger after Escape or an item choice, unless focus has
  // already moved somewhere else.
  #restoreFocus() {
    if (!this.hasTriggerTarget) return;
    const active = document.activeElement;
    if (active && active !== document.body && !this.menuTarget.contains(active))
      return;

    this.#focusTrigger();
  }

  // Focusing an input trigger opens its menu; focus moved here never does.
  #focusTrigger() {
    if (!this.hasTriggerTarget) return;
    this.#restoringFocus = true;
    try {
      this.triggerTarget.focus({ preventScroll: true });
    } finally {
      this.#restoringFocus = false;
    }
  }

  // Runs in the capture phase, so a nested dropdown stopping the key does
  // not hide it from the outer one.
  #noteTabbing = (event) => {
    if (event.key !== "Tab") return;
    this.#tabbing = true;
    if (this.#tabbingFrame) cancelAnimationFrame(this.#tabbingFrame);
    this.#tabbingFrame = requestAnimationFrame(this.#clearTabbing);
  };

  #clearTabbing = () => {
    this.#tabbing = false;
    if (this.#tabbingFrame) cancelAnimationFrame(this.#tabbingFrame);
    this.#tabbingFrame = null;
  };

  #cancelHide() {
    if (this.#hideTimer === null) return;
    clearTimeout(this.#hideTimer);
    this.#hideTimer = null;
  }

  #items() {
    return [...this.menuTarget.querySelectorAll('[role="menuitem"]')].filter(
      (item) => item.closest(HOST) === this.element,
    );
  }

  // An item can be hidden on its own (the dashboard's Edit while editing).
  // #focusItem only rewrites visible items, so an item hidden while it holds
  // tabindex="0" keeps it until the menu closes.
  #visibleItems() {
    return this.#items().filter((item) => item.checkVisibility());
  }

  #inMenu(element) {
    return this.modeValue === "menu" && this.menuTarget.contains(element);
  }

  #isMenuItem(element) {
    return (
      this.modeValue === "menu" &&
      element.matches?.('[role="menuitem"]') &&
      this.menuTarget.contains(element)
    );
  }

  #disabledItem(target) {
    const item = target.closest?.('[role="menuitem"]');
    return (
      item &&
      item.closest(HOST) === this.element &&
      item.getAttribute("aria-disabled") === "true"
    );
  }

  #syncExpanded() {
    if (!this.hasTriggerTarget) return;
    this.triggerTarget.setAttribute("aria-expanded", String(this.isOpen));
  }

  #dispatchShow() {
    return (
      this.#dispatch("show", { cancelable: true }).defaultPrevented === false
    );
  }

  #dispatch(name, { cancelable = false } = {}) {
    const trigger = this.hasTriggerTarget ? this.triggerTarget : null;
    return this.dispatch(name, { detail: { trigger }, cancelable });
  }

  get #inline() {
    return !this.menuTarget.hasAttribute("popover");
  }

  get #manual() {
    return this.menuTarget.getAttribute("popover") === "manual";
  }

  get #selfDismissing() {
    return this.#inline || this.#manual;
  }

  get #fallbackActive() {
    return (
      this.menuTarget.dataset.position === "fallback" &&
      !this.#inline &&
      this.hasTriggerTarget
    );
  }

  // Runs once the menu is open and laid out (from the toggle event). The menu
  // stays hidden until data-placed is set. Re-places on a size change of the
  // menu or trigger and on window resize, and closes on a scroll of the
  // document or of a trigger ancestor: a fixed menu would otherwise drift
  // from its trigger (see #onFallbackScroll for scrollers in open popovers).
  #startFallback() {
    if (this.#fallback || !this.#fallbackActive) return;

    const menu = this.menuTarget;
    const trigger = this.triggerTarget;
    const { cap, gap } = this.#fallbackStyle(menu);
    const observer = new ResizeObserver(this.#placeFallback);
    observer.observe(menu);
    observer.observe(trigger);
    document.addEventListener("scroll", this.#onFallbackScroll, true);
    window.addEventListener("resize", this.#placeFallback);
    this.#fallback = { menu, trigger, observer, cap, gap, applied: null };

    this.#placeFallback();
  }

  #stopFallback() {
    if (!this.#fallback) return;
    const { menu, observer } = this.#fallback;
    this.#fallback = null;

    observer.disconnect();
    document.removeEventListener("scroll", this.#onFallbackScroll, true);
    window.removeEventListener("resize", this.#placeFallback);
    menu.style.removeProperty("top");
    menu.style.removeProperty("left");
    menu.style.removeProperty("max-height");
    menu.style.removeProperty("width");
    menu.style.removeProperty("min-width");
    delete menu.dataset.placed;
  }

  // The menu's height cap (--_menu-max-height, through its max-block-size)
  // and its gap (--_gap), read once per open, before any inline max-height.
  #fallbackStyle(menu) {
    menu.style.removeProperty("max-height");
    const style = getComputedStyle(menu);
    const cap = parseFloat(style.maxHeight);
    return {
      cap: Number.isFinite(cap) ? cap : Infinity,
      gap: cssLength(style.getPropertyValue("--_gap"), style) ?? DEFAULT_GAP,
    };
  }

  // Styles are written only when a value changed: in Chrome 155 any style
  // write to an open menu resets its scrollTop.
  #placeFallback = () => {
    const state = this.#fallback;
    if (!state) return;
    const { menu, trigger, cap, gap } = state;

    const box = trigger.getBoundingClientRect();
    const triggerRect = {
      top: box.top,
      left: box.left,
      right: box.right,
      bottom: box.bottom,
      width: box.width,
    };

    const width = menu.dataset.width === "trigger" ? box.width : null;
    const minWidth = menu.dataset.minWidth === "trigger" ? box.width : null;

    // scrollHeight is the natural height even while max-height caps the box.
    const border = menu.offsetHeight - menu.clientHeight;
    const size = {
      width: menu.offsetWidth,
      height: menu.scrollHeight + border,
    };

    const { top, left, maxHeight } = fallbackPosition({
      trigger: triggerRect,
      menu: size,
      // clientWidth leaves out the page's scrollbar, as the menu's
      // max-inline-size does, so a capped menu isn't pushed under it.
      viewport: {
        width: document.documentElement.clientWidth,
        height: window.innerHeight,
      },
      align: menu.dataset.align,
      gap,
      maxHeight: cap,
    });

    const next = { top, left, maxHeight, width, minWidth };
    const last = state.applied;
    if (last && Object.keys(next).every((key) => next[key] === last[key])) {
      return;
    }
    state.applied = next;

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.maxHeight = `${maxHeight}px`;
    if (width === null) menu.style.removeProperty("width");
    else menu.style.width = `${width}px`;
    if (minWidth === null) menu.style.removeProperty("min-width");
    else menu.style.minWidth = `${minWidth}px`;
    menu.dataset.placed = "";
  };

  // Scrolls inside the menu or in unrelated containers leave it open. A
  // scroller inside an open popover, such as the parent panel of a nested
  // menu, doesn't move that popover, so the menu follows its trigger there
  // and closes only once the trigger leaves the scroller's view.
  #onFallbackScroll = (event) => {
    const target = event.target;
    const page =
      target === document ||
      target === document.documentElement ||
      target === document.scrollingElement;
    if (!page && !target.contains?.(this.triggerTarget)) return;

    if (!page && target.closest(":popover-open") && this.#triggerIn(target)) {
      this.#placeFallback();
    } else {
      this.hide();
    }
  };

  // Vertical overlap only: a horizontally scrolling panel would need a
  // horizontal check too.
  #triggerIn(scroller) {
    const trigger = this.triggerTarget.getBoundingClientRect();
    const view = scroller.getBoundingClientRect();
    return trigger.bottom > view.top && trigger.top < view.bottom;
  }

  #anchorSupported() {
    return (
      !this.forceFallbackValue &&
      typeof CSS !== "undefined" &&
      CSS.supports("position-area: bottom span-right") &&
      CSS.supports("position-try-fallbacks: flip-block")
    );
  }
}

// A px, rem or em length in pixels, or null for anything else.
function cssLength(value, style) {
  const match = /^(-?[\d.]+)(px|rem|em)$/.exec(value.trim());
  if (!match) return null;
  const number = parseFloat(match[1]);
  if (match[2] === "px") return number;
  if (match[2] === "em") return number * parseFloat(style.fontSize);
  const root = getComputedStyle(document.documentElement).fontSize;
  return number * parseFloat(root);
}
